/**
 * Modal answers to the engine's `pendingChoices`.
 *
 * The engine never guesses on the player's behalf, so every choice it raises
 * gets a prompt here: discard down to hand size, scry, order blockers, choose
 * targets for a trigger already on the stack, and sacrifice. Each renders the
 * real card frames rather than a list of names, because "which one do I keep"
 * is a decision about the cards, not about their titles.
 *
 * A prompt is not dismissible: the game is waiting on it.
 */

import { useEffect, useState } from 'react';
import type { Action, GameState, PendingChoice, TargetSlotSpec } from '@/engine';
import { profile } from '@/engine';
import { CardFrame, viewFromObject } from '@/ui/cards';
import { Button, Modal } from '@/ui/system';
import styles from './Battlefield.module.css';

export interface PromptProps {
  state: GameState;
  choice: PendingChoice;
  localPlayer: string;
  onAnswer: (action: Action) => void;
}

export function ChoicePrompt({ state, choice, localPlayer, onAnswer }: PromptProps) {
  switch (choice.kind) {
    case 'discard':
      return <DiscardPrompt state={state} choice={choice} onAnswer={onAnswer} />;
    case 'scry':
      return <ScryPrompt state={state} choice={choice} onAnswer={onAnswer} />;
    case 'orderBlockers':
      return <OrderBlockersPrompt state={state} choice={choice} onAnswer={onAnswer} />;
    case 'sacrifice':
      return <SacrificePrompt state={state} choice={choice} onAnswer={onAnswer} />;
    case 'chooseTargets':
      return <ChooseTargetsPrompt state={state} choice={choice} localPlayer={localPlayer} onAnswer={onAnswer} />;
    default:
      return null;
  }
}

/** Pick cards to discard. RULES section 3, End step hand size check. */
function DiscardPrompt({
  state,
  choice,
  onAnswer,
}: {
  state: GameState;
  choice: Extract<PendingChoice, { kind: 'discard' }>;
  onAnswer: (action: Action) => void;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const hand = state.players[choice.player]?.hand ?? [];
  const done = picked.length === choice.count;

  return (
    <Modal
      open
      dismissible={false}
      title={`Discard ${choice.count}`}
      subtitle={`Your hand is over the limit. Pick ${choice.count} card${choice.count === 1 ? '' : 's'} to send to the Dust.`}
      size="large"
      footer={
        <Button
          variant="primary"
          disabled={!done}
          onClick={() => onAnswer({ type: 'Discard', player: choice.player, cardInstanceIds: picked })}
        >
          Discard {picked.length} of {choice.count}
        </Button>
      }
    >
      <div className={styles.promptCards}>
        {hand.map((id) => {
          const view = viewFromObject(state, id);
          if (!view) return null;
          const on = picked.includes(id);
          return (
            <CardFrame
              key={id}
              card={view}
              size="hand"
              flat
              selected={on}
              dimmed={!on && done}
              onClick={() =>
                setPicked((list) =>
                  list.includes(id) ? list.filter((x) => x !== id) : list.length < choice.count ? [...list, id] : list,
                )
              }
            />
          );
        })}
      </div>
    </Modal>
  );
}

/** Look at the top N and choose which go to the bottom. RULES, `scry`. */
function ScryPrompt({
  state,
  choice,
  onAnswer,
}: {
  state: GameState;
  choice: Extract<PendingChoice, { kind: 'scry' }>;
  onAnswer: (action: Action) => void;
}) {
  const [bottom, setBottom] = useState<string[]>([]);

  return (
    <Modal
      open
      dismissible={false}
      title="Scry"
      subtitle="Cards you send to the bottom go in the order you picked them. The rest stay on top."
      size="large"
      footer={
        <Button
          variant="primary"
          onClick={() => onAnswer({ type: 'Scry', player: choice.player, toBottom: bottom })}
        >
          {bottom.length === 0 ? 'Keep all on top' : `Bottom ${bottom.length}`}
        </Button>
      }
    >
      <div className={styles.promptCards}>
        {choice.cards.map((id) => {
          const view = viewFromObject(state, id);
          if (!view) return null;
          const index = bottom.indexOf(id);
          return (
            <div key={id} style={{ textAlign: 'center' }}>
              <CardFrame
                card={view}
                size="hand"
                flat
                selected={index >= 0}
                onClick={() =>
                  setBottom((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]))
                }
              />
              <div className={styles.abilityDetail}>
                {index >= 0 ? `To bottom, position ${index + 1}` : 'Stays on top'}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

/**
 * Order the blockers on one attacker. RULES clarification 5: this is a real
 * choice, because the attacker must assign lethal damage in this order.
 */
function OrderBlockersPrompt({
  state,
  choice,
  onAnswer,
}: {
  state: GameState;
  choice: Extract<PendingChoice, { kind: 'orderBlockers' }>;
  onAnswer: (action: Action) => void;
}) {
  const [order, setOrder] = useState<string[]>(choice.blockerIds);

  useEffect(() => setOrder(choice.blockerIds), [choice.blockerIds]);

  const move = (index: number, delta: number) => {
    setOrder((list) => {
      const next = list.slice();
      const target = index + delta;
      if (target < 0 || target >= next.length) return list;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  return (
    <Modal
      open
      dismissible={false}
      title="Order the blockers"
      subtitle={`${profile(state, choice.attackerId).name} assigns lethal damage in this order before any damage spills past.`}
      footer={
        <Button
          variant="primary"
          onClick={() =>
            onAnswer({ type: 'OrderBlockers', player: choice.player, attacker: choice.attackerId, order })
          }
        >
          Confirm order
        </Button>
      }
    >
      <div className={styles.orderList}>
        {order.map((id, i) => (
          <div key={id} className={styles.orderRow}>
            <span className={styles.orderIndex}>{i + 1}</span>
            <span className={styles.orderName}>{profile(state, id).name}</span>
            <Button size="small" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)}>
              Up
            </Button>
            <Button size="small" variant="ghost" disabled={i === order.length - 1} onClick={() => move(i, 1)}>
              Down
            </Button>
          </div>
        ))}
      </div>
    </Modal>
  );
}

/** Choose your own permanents to sacrifice. */
function SacrificePrompt({
  state,
  choice,
  onAnswer,
}: {
  state: GameState;
  choice: Extract<PendingChoice, { kind: 'sacrifice' }>;
  onAnswer: (action: Action) => void;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const done = picked.length === Math.min(choice.count, choice.legal.length);

  return (
    <Modal
      open
      dismissible={false}
      title={`Sacrifice ${choice.count}`}
      subtitle="Pick which of your permanents goes to the Dust."
      size="large"
      footer={
        <Button
          variant="primary"
          disabled={!done}
          onClick={() => onAnswer({ type: 'Sacrifice', player: choice.player, cardInstanceIds: picked })}
        >
          Sacrifice {picked.length}
        </Button>
      }
    >
      <div className={styles.promptCards}>
        {choice.legal.map((id) => {
          const view = viewFromObject(state, id);
          if (!view) return null;
          const on = picked.includes(id);
          return (
            <CardFrame
              key={id}
              card={view}
              size="hand"
              flat
              selected={on}
              onClick={() =>
                setPicked((list) =>
                  list.includes(id) ? list.filter((x) => x !== id) : list.length < choice.count ? [...list, id] : list,
                )
              }
            />
          );
        })}
      </div>
    </Modal>
  );
}

/**
 * Targets for an item already on the stack, which is how a permanent's deploy
 * trigger asks (RULES clarification 2). Shared slots are one prompt each,
 * because the engine already collapsed them (clarification 16).
 */
function ChooseTargetsPrompt({
  state,
  choice,
  onAnswer,
}: {
  state: GameState;
  choice: Extract<PendingChoice, { kind: 'chooseTargets' }>;
  localPlayer: string;
  onAnswer: (action: Action) => void;
}) {
  const [picks, setPicks] = useState<string[][]>(() => choice.slots.map(() => []));

  useEffect(() => setPicks(choice.slots.map(() => [])), [choice.slots]);

  const complete = choice.slots.every(
    (slot, i) => slot.optional || picks[i]?.length === Math.min(slot.count, slot.legal.length),
  );

  return (
    <Modal
      open
      dismissible={false}
      title="Choose targets"
      subtitle={`${labelOfStackItem(state, choice.stackItemId)} needs a target.`}
      size="medium"
      footer={
        <Button
          variant="primary"
          disabled={!complete}
          onClick={() =>
            onAnswer({
              type: 'ChooseTargets',
              player: choice.player,
              stackItemId: choice.stackItemId,
              targets: picks,
            })
          }
        >
          Confirm
        </Button>
      }
    >
      <div className={styles.promptTargets}>
        {choice.slots.map((slot, slotIndex) => (
          <SlotPicker
            key={`${slot.slot ?? 'slot'}-${slotIndex}`}
            state={state}
            slot={slot}
            picked={picks[slotIndex] ?? []}
            onToggle={(id) =>
              setPicks((list) => {
                const next = list.map((p) => p.slice());
                const current = next[slotIndex] ?? [];
                next[slotIndex] = current.includes(id)
                  ? current.filter((x) => x !== id)
                  : current.length < slot.count
                    ? [...current, id]
                    : [...current.slice(1), id];
                return next;
              })
            }
          />
        ))}
      </div>
    </Modal>
  );
}

function SlotPicker({
  state,
  slot,
  picked,
  onToggle,
}: {
  state: GameState;
  slot: TargetSlotSpec;
  picked: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div>
      <div className={styles.abilityDetail}>
        {slot.type}
        {slot.count > 1 ? `, choose ${slot.count}` : ''}
        {slot.optional ? ', optional' : ''}
      </div>
      {slot.legal.length === 0 ? (
        <p className={styles.emptyNote}>No legal target.</p>
      ) : (
        slot.legal.map((id) => (
          <button
            key={id}
            type="button"
            className={[styles.targetOption, picked.includes(id) ? styles.targetOptionOn : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onToggle(id)}
          >
            {labelForTarget(state, id)}
          </button>
        ))
      )}
    </div>
  );
}

function labelForTarget(state: GameState, id: string): string {
  const player = state.players[id];
  if (player) return player.name;
  return profile(state, id).name;
}

function labelOfStackItem(state: GameState, stackItemId: string): string {
  return state.stack.find((item) => item.id === stackItemId)?.label ?? 'An effect';
}
